import React, { useState, useEffect, useCallback } from 'react';
import { Container, Typography, Button, Modal, Box, TextField, CircularProgress, IconButton } from '@mui/material';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';

import config from '../../config';
import { encryptData } from '../../utils/cryptoUtils';

function ResellManagementRepair() {
  const userInfo = useSelector((state) => state.userInfo);
  const userId = userInfo?.id;

  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState('');
  const [tableData, setTableData] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);

  // Delete confirm modal state
  const [deleteId, setDeleteId] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const handleClose = () => {
    if (submitLoading) return;
    setOpen(false);
    setAmount('');
  };

  const fetchRepairCostList = useCallback(async () => {
    setListLoading(true);

    try {
      const res = await fetch(`${config.API_BASE_URL}getRepoRepairCostList`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: encryptData({}),
      });

      const result = await res.json();

      if (result.status) {
        setTableData(result.data || []);
      } else {
        setTableData([]);
        if (result.message) toast.error(result.message);
      }
    } catch (err) {
      toast.error('Failed to load repair cost list');
    } finally {
      setListLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRepairCostList();
  }, [fetchRepairCostList]);

  const handleSubmit = async () => {
    if (!amount || Number(amount) <= 0) {
      toast.warning('Please enter a valid amount');
      return;
    }

    if (!userId) {
      toast.error('Unable to identify logged in user');
      return;
    }

    setSubmitLoading(true);

    try {
      const payload = {
        amount: Number(amount),
        user_id: userId,
      };

      const res = await fetch(`${config.API_BASE_URL}saveRepoRepairCost`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: encryptData(payload),
      });

      const result = await res.json();

      if (result.status) {
        toast.success(result.message || 'Repair cost added successfully');
        handleClose();
        fetchRepairCostList();
      } else {
        toast.error(result.message || 'Something went wrong');
      }
    } catch (err) {
      toast.error('Something went wrong');
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleDeleteClick = (id) => {
    setDeleteId(id);
  };

  const handleDeleteCancel = () => {
    if (deleteLoading) return;
    setDeleteId(null);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteId) return;

    if (!userId) {
      toast.error('Unable to identify logged in user');
      return;
    }

    setDeleteLoading(true);

    try {
      const payload = {
        id: deleteId,
        user_id: userId,
      };

      const res = await fetch(`${config.API_BASE_URL}deleteRepoRepairCost`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: encryptData(payload),
      });

      const result = await res.json();

      if (result.status) {
        toast.success(result.message || 'Repair cost deleted successfully');
        setDeleteId(null);
        fetchRepairCostList();
      } else {
        toast.error(result.message || 'Something went wrong');
      }
    } catch (err) {
      toast.error('Something went wrong');
    } finally {
      setDeleteLoading(false);
    }
  };

  const formatDateTime = (date) => {
    if (!date) return '-';

    const d = new Date(date);

    const day = d.toLocaleString('en-IN', { day: '2-digit' });
    const month = d.toLocaleString('en-IN', { month: 'long' });
    const year = d.toLocaleString('en-IN', { year: 'numeric' });
    const time = d.toLocaleString('en-IN', { hour: '2-digit', minute: '2-digit' });

    return `${day} ${month}, ${year} - ${time}`;
  };

  return (
    <>
      <Container maxWidth="xl">
        <div className="d-flex align-items-center justify-content-between mb-4">
          <Typography variant="h4">Resell Repair Amount Master</Typography>

          <Button variant="contained" onClick={() => setOpen(true)}>
            + Add / Update Repair Cost
          </Button>
        </div>

        <div className="table-wrap">
          <table className="table table-hover table-bordered">
            <thead>
              <tr>
                <th style={{ width: '80px' }}>S.No.</th>
                <th>Repair Approval Amount</th>
                <th>Created By</th>
                <th>Created At</th>
                <th style={{ width: '90px' }}>Action</th>
              </tr>
            </thead>

            <tbody>
              {listLoading ? (
                <tr>
                  <td colSpan={5} className="text-center py-4">
                    <CircularProgress size={22} />
                  </td>
                </tr>
              ) : tableData.length === 0 ? (
                <tr>
                  <td colSpan={5} className="text-center">
                    No repair cost found
                  </td>
                </tr>
              ) : (
                tableData.map((item, index) => (
                  <tr key={item.id}>
                    <td>{index + 1}</td>
                    <td>₹{Number(item.amount).toLocaleString('en-IN')}</td>
                    <td>{item.created_by_name || '-'}</td>
                    <td>{formatDateTime(item.created_at)}</td>
                    <td className="text-center">
                      <IconButton size="small" color="error" onClick={() => handleDeleteClick(item.id)}>
                        <i className="bi bi-trash" style={{ fontSize: '16px' }} />
                      </IconButton>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Container>

      {/* Add Repair Cost Modal */}
      <Modal open={open} onClose={handleClose}>
        <Box
          sx={{
            width: 500,
            maxWidth: '90%',
            bgcolor: '#fff',
            p: 3,
            borderRadius: 2,
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            boxShadow: 24,
          }}
        >
          <Typography variant="h5" className="mb-3">
            Add Repair Approval Amount
          </Typography>

          <TextField
            fullWidth
            type="number"
            label="Repair Amount"
            placeholder="Enter repair amount"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            disabled={submitLoading}
            inputProps={{ min: 0 }}
            sx={{ mb: 3 }}
          />

          <div className="d-flex justify-content-end gap-2">
            <Button variant="outlined" onClick={handleClose} disabled={submitLoading}>
              Cancel
            </Button>

            <Button variant="contained" onClick={handleSubmit} disabled={submitLoading}>
              {submitLoading ? 'Submitting...' : 'Submit'}
            </Button>
          </div>
        </Box>
      </Modal>

      {/* Delete Confirm Modal — small, SweetAlert-style */}
      <Modal open={Boolean(deleteId)} onClose={handleDeleteCancel}>
        <Box
          sx={{
            width: 340,
            maxWidth: '90%',
            bgcolor: '#fff',
            p: 3,
            borderRadius: 2,
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            boxShadow: 24,
            textAlign: 'center',
          }}
        >
          <i
            className="bi bi-exclamation-triangle"
            style={{
              fontSize: '40px',
              color: '#ed6c02',
              marginBottom: '8px',
              display: 'inline-block',
            }}
          />

          <Typography variant="h6" sx={{ mb: 0.5 }}>
            Are you sure?
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
            You won't be able to revert this!
          </Typography>

          <div className="d-flex justify-content-center gap-2">
            <Button variant="outlined" color="error" size="small" onClick={handleDeleteCancel} disabled={deleteLoading}>
              Cancel
            </Button>

            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={handleDeleteConfirm}
              disabled={deleteLoading}
            >
              {deleteLoading ? 'Deleting...' : 'Yes, delete it!'}
            </Button>
          </div>
        </Box>
      </Modal>
    </>
  );
}

export default ResellManagementRepair;
